import { useEffect, useMemo } from "react";
import type { Locale } from "../types/app";

const SITE_ORIGIN = "https://issamawali.com";
const SITE_BASE_PATH = "/athkari";
const SITE_URL = `${SITE_ORIGIN}${SITE_BASE_PATH}`;
const SITE_IMAGE = `${SITE_ORIGIN}/assets/icons/icon-512.png`;
const BRAND = { ar: "أذكاري", en: "Athkari" } as const;
const CREATOR = { ar: "عيسى المعولي", en: "Issa Al-Mawali" } as const;

type LocalizedCopy = {
  readonly ar: string;
  readonly en: string;
};

type SeoDefinition = {
  readonly title: LocalizedCopy;
  readonly description: LocalizedCopy;
  readonly keywords: readonly string[];
  readonly topics: readonly LocalizedCopy[];
  readonly pageType?: "WebPage" | "CollectionPage" | "AboutPage" | "ContactPage" | "ProfilePage";
  readonly robots?: string;
};

const coreKeywords = [
  "Athkari",
  "أذكاري",
  "Issa Al-Mawali",
  "عيسى المعولي",
  "Islamic app",
  "تطبيق إسلامي",
  "Quran",
  "القرآن الكريم",
  "Athkar",
  "أذكار",
  "Adhkar",
  "Prayer times",
  "مواقيت الصلاة",
  "Qibla compass",
  "بوصلة القبلة",
  "Moon phase",
  "أطوار القمر",
  "Duas",
  "الأدعية",
  "Tasbih",
  "التسبيح",
  "Manasik",
  "المناسك"
] as const;

const routeSeo: Record<string, SeoDefinition> = {
  "/": {
    title: {
      ar: "أذكاري للقرآن والأذكار ومواقيت الصلاة والقبلة والقمر",
      en: "Athkari for Quran, Athkar, Prayer Times, Qibla and Moon"
    },
    description: {
      ar: "أذكاري من عيسى المعولي منصة إسلامية ثنائية اللغة تجمع الأذكار اليومية والقرآن الكريم ومواقيت الصلاة وبوصلة القبلة وأدوات القمر والأدعية والتسبيح والمناسك في تجربة هادئة وسريعة.",
      en: "Athkari by Issa Al-Mawali is a bilingual Islamic web app for daily Athkar, Quran reading and audio, prayer times, Qibla compass, moon tools, Duas, Tasbih, and Manasik in a calm modern experience."
    },
    keywords: ["Quran app", "Athkar app", "Islamic utilities", "تطبيق القرآن", "تطبيق الأذكار", "القبلة", "القمر"],
    topics: [
      { ar: "القرآن الكريم", en: "Quran" },
      { ar: "الأذكار اليومية", en: "Daily Athkar" },
      { ar: "مواقيت الصلاة", en: "Prayer Times" },
      { ar: "بوصلة القبلة", en: "Qibla Compass" },
      { ar: "أطوار القمر", en: "Moon Phases" }
    ],
    pageType: "CollectionPage"
  },
  "/athkar": {
    title: {
      ar: "أذكار الصباح والمساء والنوم والسفر",
      en: "Morning, Evening, Sleep and Travel Athkar"
    },
    description: {
      ar: "تصفح أذكار الصباح والمساء والنوم والسفر في أذكاري مع حفظ التقدم وخيارات خط عربية مناسبة للنصوص والآيات.",
      en: "Browse morning, evening, sleep, and travel Athkar in Athkari with saved progress and Arabic-friendly font options for supplications and Quran text."
    },
    keywords: ["Morning Athkar", "Evening Athkar", "Sleep Athkar", "Travel Athkar", "أذكار الصباح", "أذكار المساء"],
    topics: [
      { ar: "أذكار الصباح", en: "Morning Athkar" },
      { ar: "أذكار المساء", en: "Evening Athkar" },
      { ar: "أذكار النوم", en: "Sleep Athkar" }
    ],
    pageType: "CollectionPage"
  },
  "/quran": {
    title: {
      ar: "القرآن الكريم قراءة واستماع",
      en: "Quran Reader and Audio"
    },
    description: {
      ar: "اقرأ القرآن الكريم واستمع للتلاوات مع واجهة ثنائية اللغة ومرجع سريع للسور والآيات داخل أذكاري.",
      en: "Read the Holy Quran and listen to recitations with a bilingual interface and quick Surah and Ayah access inside Athkari."
    },
    keywords: ["Quran reader", "Quran audio", "Surah", "Ayah", "تلاوة القرآن", "سور القرآن"],
    topics: [
      { ar: "القرآن الكريم", en: "Quran" },
      { ar: "التلاوة الصوتية", en: "Audio Recitation" },
      { ar: "السور والآيات", en: "Surahs and Ayat" }
    ],
    pageType: "CollectionPage"
  },
  "/favorites": {
    title: {
      ar: "المفضلة المحفوظة من الأذكار والقرآن",
      en: "Saved Favorites from Athkar and Quran"
    },
    description: {
      ar: "ارجع بسرعة إلى العناصر المحفوظة من الأذكار والآيات والمقاطع المهمة داخل أذكاري.",
      en: "Quickly return to your saved Athkar, Ayat, and important items inside Athkari."
    },
    keywords: ["Saved favorites", "Favorites Quran", "Favorites Athkar", "المفضلة", "حفظ الأذكار"],
    topics: [
      { ar: "المفضلة", en: "Favorites" },
      { ar: "الآيات المحفوظة", en: "Saved Ayat" }
    ],
    pageType: "CollectionPage"
  },
  "/prayer": {
    title: {
      ar: "مواقيت الصلاة والعد التنازلي للصلاة التالية",
      en: "Prayer Times and Next Prayer Countdown"
    },
    description: {
      ar: "اعرض مواقيت الصلاة حسب موقعك أو الإحداثيات اليدوية مع العد التنازلي للصلاة التالية وخيارات الحساب المختلفة.",
      en: "View prayer times by current location or manual coordinates with a next-prayer countdown and multiple calculation methods."
    },
    keywords: ["Prayer times", "Next prayer", "Asr", "Fajr", "مواقيت الصلاة اليوم", "الصلاة التالية"],
    topics: [
      { ar: "مواقيت الصلاة", en: "Prayer Times" },
      { ar: "الموقع الحالي", en: "Current Location" },
      { ar: "الحساب الفقهي", en: "Calculation Method" }
    ],
    pageType: "CollectionPage"
  },
  "/qibla": {
    title: {
      ar: "بوصلة القبلة واتجاه الكعبة",
      en: "Qibla Compass and Kaaba Direction"
    },
    description: {
      ar: "استخدم بوصلة القبلة لتحديد اتجاه الكعبة من موقعك الحالي داخل أذكاري مع واجهة عربية وإنجليزية واضحة.",
      en: "Use the Qibla compass to find the Kaaba direction from your current location in Athkari with a clear Arabic and English interface."
    },
    keywords: ["Qibla compass", "Kaaba direction", "Compass", "اتجاه القبلة", "بوصلة إسلامية"],
    topics: [
      { ar: "بوصلة القبلة", en: "Qibla Compass" },
      { ar: "اتجاه الكعبة", en: "Kaaba Direction" }
    ],
    pageType: "CollectionPage"
  },
  "/moon": {
    title: {
      ar: "أطوار القمر والعمر القمري ونسبة الإضاءة",
      en: "Moon Phase, Lunar Age and Illumination"
    },
    description: {
      ar: "تابع طور القمر الحالي ونسبة الإضاءة والعمر القمري من خلال بطاقة قمر حديثة داخل أذكاري.",
      en: "Track the current moon phase, illumination percentage, and lunar age through a modern moon card inside Athkari."
    },
    keywords: ["Moon phase", "Lunar age", "Moon illumination", "طور القمر", "العمر القمري"],
    topics: [
      { ar: "طور القمر", en: "Moon Phase" },
      { ar: "الإضاءة", en: "Illumination" },
      { ar: "العمر القمري", en: "Lunar Age" }
    ],
    pageType: "CollectionPage"
  },
  "/manasik": {
    title: {
      ar: "المناسك وخريطة الحج والعمرة التفاعلية",
      en: "Manasik and Interactive Hajj and Umrah Map"
    },
    description: {
      ar: "استعرض خطوات المناسك عبر خريطة تفاعلية عربية واضحة للحج والعمرة مع المراحل الأساسية والتنقل بينها.",
      en: "Explore Hajj and Umrah steps through a clear interactive Arabic-friendly Manasik map with key stages and navigation."
    },
    keywords: ["Hajj map", "Umrah guide", "Manasik map", "مناسك الحج", "مناسك العمرة"],
    topics: [
      { ar: "الحج", en: "Hajj" },
      { ar: "العمرة", en: "Umrah" },
      { ar: "خريطة المناسك", en: "Manasik Map" }
    ],
    pageType: "CollectionPage"
  },
  "/duas": {
    title: {
      ar: "الأدعية الإسلامية المختارة",
      en: "Selected Islamic Duas"
    },
    description: {
      ar: "اطلع على الأدعية الإسلامية المختارة داخل أذكاري في عرض منظّم وسريع مناسب للهواتف والويب.",
      en: "Browse selected Islamic Duas inside Athkari in a structured and fast experience designed for mobile and web."
    },
    keywords: ["Islamic Duas", "Supplications", "دعاء", "أدعية إسلامية"],
    topics: [
      { ar: "الأدعية", en: "Duas" },
      { ar: "الأذكار", en: "Supplications" }
    ],
    pageType: "CollectionPage"
  },
  "/tasbih": {
    title: {
      ar: "التسبيح الرقمي والعداد المستقر",
      en: "Digital Tasbih Counter"
    },
    description: {
      ar: "استخدم عداد التسبيح الرقمي في أذكاري مع عرض واضح للأرقام وحساب دقيق ومستقر على الجوال والويب.",
      en: "Use Athkari’s digital Tasbih counter with clear number display and accurate stable counting across mobile and web."
    },
    keywords: ["Digital Tasbih", "Tasbih counter", "عداد التسبيح", "سبحان الله"],
    topics: [
      { ar: "التسبيح", en: "Tasbih" },
      { ar: "العداد الرقمي", en: "Digital Counter" }
    ],
    pageType: "CollectionPage"
  },
  "/alkhalili": {
    title: {
      ar: "الشيخ أحمد بن حمد الخليلي والمكتبة المرئية",
      en: "Sheikh Ahmed bin Hamad Al-Khalili Library"
    },
    description: {
      ar: "صفحة منظمة لسماحة الشيخ أحمد بن حمد الخليلي تضم نبذة موثوقة وروابط رسمية ومكتبة مرئية داخل أذكاري.",
      en: "An organized profile for Sheikh Ahmed bin Hamad Al-Khalili with trusted references, official links, and a visual library inside Athkari."
    },
    keywords: ["Ahmed bin Hamad Al-Khalili", "Sheikh Al-Khalili", "الشيخ أحمد بن حمد الخليلي", "مفتي سلطنة عمان"],
    topics: [
      { ar: "الشيخ أحمد بن حمد الخليلي", en: "Sheikh Ahmed bin Hamad Al-Khalili" },
      { ar: "مكتبة مرئية", en: "Visual Library" }
    ],
    pageType: "ProfilePage"
  },
  "/donate": {
    title: {
      ar: "الزكاة والصدقة وروابط التبرع الرسمية",
      en: "Zakah, Sadaqah and Official Donation Links"
    },
    description: {
      ar: "اعرض روابط الزكاة والصدقة والتبرع الرسمية من مصادر موثوقة ضمن قسم خاص في أذكاري.",
      en: "Discover trusted official links for Zakah, Sadaqah, and charitable donations in Athkari’s dedicated donation section."
    },
    keywords: ["Zakah", "Sadaqah", "Charity links", "الزكاة", "الصدقة", "تبرع رسمي"],
    topics: [
      { ar: "الزكاة", en: "Zakah" },
      { ar: "الصدقة", en: "Sadaqah" },
      { ar: "روابط موثوقة", en: "Trusted Links" }
    ],
    pageType: "CollectionPage"
  },
  "/about": {
    title: {
      ar: "عن أذكاري وعن عيسى المعولي",
      en: "About Athkari and Issa Al-Mawali"
    },
    description: {
      ar: "تعرّف على تطبيق أذكاري ورسالة المشروع والخدمات الإسلامية التي يقدمها عيسى المعولي بالعربية والإنجليزية.",
      en: "Learn about Athkari, the product vision, and the Islamic services created by Issa Al-Mawali in Arabic and English."
    },
    keywords: ["About Athkari", "Issa Al-Mawali", "عيسى المعولي", "Islamic product designer"],
    topics: [
      { ar: "عن التطبيق", en: "About the App" },
      { ar: "عيسى المعولي", en: "Issa Al-Mawali" }
    ],
    pageType: "AboutPage"
  },
  "/contact": {
    title: {
      ar: "التواصل مع أذكاري ومع عيسى المعولي",
      en: "Contact Athkari and Issa Al-Mawali"
    },
    description: {
      ar: "تواصل مع أذكاري لإرسال اقتراح أو ملاحظة حول القرآن أو الأذكار أو الصلاة أو القبلة أو القمر أو بقية الخدمات.",
      en: "Contact Athkari to share suggestions or feedback about Quran, Athkar, prayer times, Qibla, moon tools, and the rest of the app’s services."
    },
    keywords: ["Contact Athkari", "Contact Issa Al-Mawali", "تواصل أذكاري", "اقتراحات التطبيق"],
    topics: [
      { ar: "التواصل", en: "Contact" },
      { ar: "الدعم", en: "Support" }
    ],
    pageType: "ContactPage"
  },
  "__not_found__": {
    title: {
      ar: "الصفحة غير موجودة",
      en: "Page Not Found"
    },
    description: {
      ar: "الصفحة المطلوبة غير متوفرة داخل أذكاري. تصفح القرآن والأذكار والصلاة والقبلة والقمر من الروابط الرئيسية.",
      en: "The requested page is not available in Athkari. Explore Quran, Athkar, prayer times, Qibla, and moon tools from the main navigation."
    },
    keywords: ["Page not found", "404", "الصفحة غير موجودة"],
    topics: [
      { ar: "الصفحة غير موجودة", en: "Page Not Found" }
    ],
    pageType: "WebPage",
    robots: "noindex, nofollow"
  }
};

type ResolvedSeo = {
  readonly title: string;
  readonly description: string;
  readonly keywords: string;
  readonly url: string;
  readonly pageType: NonNullable<SeoDefinition["pageType"]>;
  readonly robots: string;
  readonly locale: string;
  readonly localeAlternate: string;
  readonly label: string;
  readonly topics: readonly string[];
};

function getCanonicalUrl(route: string) {
  if (route === "/") return SITE_URL;
  const normalized = route.replace(/^\/+/, "");
  return `${SITE_URL}/${normalized}`;
}

function dedupe(values: readonly string[]) {
  return [...new Set(values.filter(Boolean))];
}

function resolveSeo(route: string, locale: Locale): ResolvedSeo {
  const fallbackDefinition = routeSeo["__not_found__"] as SeoDefinition;
  const definition = (routeSeo[route] ?? fallbackDefinition) as SeoDefinition;
  const canonicalRoute = route === "__not_found__" ? "/" : route;
  const label = locale === "ar" ? definition.title.ar : definition.title.en;
  const brand = locale === "ar" ? BRAND.ar : BRAND.en;
  const description = locale === "ar" ? definition.description.ar : definition.description.en;
  const localeTopics = definition.topics.map((topic) => locale === "ar" ? topic.ar : topic.en);
  const keywords = dedupe([
    ...coreKeywords,
    ...definition.keywords,
    ...definition.topics.map((topic) => topic.ar),
    ...definition.topics.map((topic) => topic.en),
    BRAND.ar,
    BRAND.en,
    CREATOR.ar,
    CREATOR.en
  ]);

  return {
    title: `${label} | ${brand}`,
    description,
    keywords: keywords.join(", "),
    url: getCanonicalUrl(canonicalRoute),
    pageType: definition.pageType ?? "WebPage",
    robots: definition.robots ?? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    locale: locale === "ar" ? "ar_OM" : "en_US",
    localeAlternate: locale === "ar" ? "en_US" : "ar_OM",
    label,
    topics: localeTopics
  };
}

function ensureMeta(attribute: "name" | "property", key: string, content: string) {
  let node = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(attribute, key);
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

function ensureLink(rel: string, href: string) {
  let node = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", rel);
    document.head.appendChild(node);
  }
  node.setAttribute("href", href);
}

function ensureJsonLd(id: string, payload: unknown) {
  let node = document.head.querySelector<HTMLScriptElement>(`script#${id}`);
  if (!node) {
    node = document.createElement("script");
    node.id = id;
    node.type = "application/ld+json";
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify(payload);
}

function buildStructuredData(meta: ResolvedSeo) {
  const creatorId = `${SITE_URL}#creator`;
  const websiteId = `${SITE_URL}#website`;
  const appId = `${SITE_URL}#app`;
  const pageId = `${meta.url}#page`;
  const creatorName = `${CREATOR.en} | ${CREATOR.ar}`;

  const creator = {
    "@type": "Person",
    "@id": creatorId,
    name: CREATOR.en,
    alternateName: CREATOR.ar,
    url: SITE_URL,
    worksFor: {
      "@type": "Organization",
      name: `${BRAND.en} | ${BRAND.ar}`
    }
  };

  const website = {
    "@type": "WebSite",
    "@id": websiteId,
    url: SITE_URL,
    name: BRAND.en,
    alternateName: BRAND.ar,
    description: meta.description,
    inLanguage: ["ar", "en"],
    publisher: { "@id": creatorId },
    creator: { "@id": creatorId }
  };

  const app = {
    "@type": "SoftwareApplication",
    "@id": appId,
    name: BRAND.en,
    alternateName: BRAND.ar,
    url: SITE_URL,
    image: SITE_IMAGE,
    applicationCategory: "LifestyleApplication",
    operatingSystem: "Web Browser, Android, iOS, PWA",
    inLanguage: ["ar", "en"],
    creator: { "@id": creatorId },
    publisher: { "@id": creatorId },
    description: meta.description,
    featureList: dedupe([
      "Quran reader",
      "Quran audio",
      "Athkar",
      "Prayer times",
      "Qibla compass",
      "Moon phase",
      "Duas",
      "Tasbih counter",
      "Manasik guide",
      "القرآن الكريم",
      "الأذكار",
      "مواقيت الصلاة",
      "بوصلة القبلة",
      "أطوار القمر",
      "الأدعية",
      "التسبيح",
      "المناسك"
    ]),
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock"
    }
  };

  const webpage = {
    "@type": meta.pageType,
    "@id": pageId,
    url: meta.url,
    name: meta.title,
    headline: meta.label,
    description: meta.description,
    inLanguage: meta.locale.startsWith("ar") ? "ar" : "en",
    isPartOf: { "@id": websiteId },
    about: meta.topics.map((topic) => ({ "@type": "Thing", name: topic })),
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: SITE_IMAGE
    },
    creator: { "@id": creatorId },
    author: { "@id": creatorId },
    publisher: { "@id": creatorId },
    keywords: meta.keywords
  };

  const breadcrumb = meta.url === SITE_URL
    ? null
    : {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: `${BRAND.en} | ${BRAND.ar}`,
            item: SITE_URL
          },
          {
            "@type": "ListItem",
            position: 2,
            name: meta.label,
            item: meta.url
          }
        ]
      };

  return {
    "@context": "https://schema.org",
    "@graph": breadcrumb ? [creator, website, app, webpage, breadcrumb] : [creator, website, app, webpage]
  };
}

function applySeo(route: string, locale: Locale) {
  const meta = resolveSeo(route, locale);
  const schema = buildStructuredData(meta);

  document.title = meta.title;
  ensureMeta("name", "description", meta.description);
  ensureMeta("name", "keywords", meta.keywords);
  ensureMeta("name", "author", `${CREATOR.en} | ${CREATOR.ar}`);
  ensureMeta("name", "creator", `${CREATOR.en} | ${CREATOR.ar}`);
  ensureMeta("name", "publisher", `${BRAND.en} | ${BRAND.ar}`);
  ensureMeta("name", "robots", meta.robots);
  ensureMeta("name", "googlebot", meta.robots);
  ensureMeta("name", "application-name", `${BRAND.en} | ${BRAND.ar}`);
  ensureMeta("name", "apple-mobile-web-app-title", `${BRAND.en} | ${BRAND.ar}`);

  ensureMeta("property", "og:type", meta.url === SITE_URL ? "website" : "article");
  ensureMeta("property", "og:site_name", `${BRAND.en} | ${BRAND.ar}`);
  ensureMeta("property", "og:title", meta.title);
  ensureMeta("property", "og:description", meta.description);
  ensureMeta("property", "og:url", meta.url);
  ensureMeta("property", "og:image", SITE_IMAGE);
  ensureMeta("property", "og:image:alt", `${BRAND.en} | ${BRAND.ar}`);
  ensureMeta("property", "og:locale", meta.locale);
  ensureMeta("property", "og:locale:alternate", meta.localeAlternate);

  ensureMeta("name", "twitter:card", "summary_large_image");
  ensureMeta("name", "twitter:title", meta.title);
  ensureMeta("name", "twitter:description", meta.description);
  ensureMeta("name", "twitter:image", SITE_IMAGE);
  ensureMeta("name", "twitter:image:alt", `${BRAND.en} | ${BRAND.ar}`);

  ensureLink("canonical", meta.url);
  ensureJsonLd("athkari-seo-schema", schema);
}

export function useRouteSeo(route: string, locale: Locale) {
  const resolvedRoute = useMemo(() => routeSeo[route] ? route : "__not_found__", [route]);

  useEffect(() => {
    applySeo(resolvedRoute, locale);
  }, [locale, resolvedRoute]);
}
